import { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { logger as LOG } from '@garnish/logger';

import { type HostedFrameMessageEvent, useNoticeBannersStackContext } from '@sg/garnish';

LOG.enable('STRIPE');
const logger = LOG.extend('STRIPE');

export const useStripeMessages = (props: StripeMessagesHandlerProps) => {
  const { onSuccess, onError } = props;

  const [canSubmitFormState, setCanSubmitForm] = useState(false);
  const [isSubmittingStripe, setIsSubmittingStripe] = useState(false);
  const handleStripeError = useStripeErrorHandling({ onError });

  const handleStripeMessages = useCallback(
    (event: HostedFrameMessageEvent) => {
      const message = parseStripeMessage(event);
      const { paymentMethodId, error, saving, canSubmitForm } = message;

      // Generic logging data from stripe.
      logger.info(message);

      // Clear saving flag.
      setIsSubmittingStripe(false);

      // Error handling from Stripe (Banner + Telemetry).
      if (error) handleStripeError(error);

      // Credit card is being tokenized.
      if (saving) setIsSubmittingStripe(true);

      // Payment Method ID generated by the submit function from stripe.
      if (paymentMethodId) onSuccess(paymentMethodId);

      // Field completion status updates
      if (canSubmitForm !== undefined) {
        setCanSubmitForm(canSubmitForm);
      }
    },
    [handleStripeError, onSuccess],
  );

  return {
    isSubmittingStripe,
    canSubmitForm: canSubmitFormState,
    handleStripeMessages,
  };
};

const useStripeErrorHandling = ({ onError }: Pick<StripeMessagesHandlerProps, 'onError'>) => {
  const { formatMessage } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  return useCallback(
    (error: Readonly<{ message?: string }>) => {
      logger.error(error);

      addNoticeBanner({
        text: error?.message ?? formatMessage(messages.creditCardFormErrorSave),
        palette: 'caution',
      });

      onError(formatMessage(messages.creditCardFormErrorSave), `Stripe: ${error?.message}`);
    },
    [formatMessage, addNoticeBanner, onError],
  );
};

const parseStripeMessage = (event: HostedFrameMessageEvent) => {
  const dataString = event.nativeEvent.data ?? '{}';
  const parsedData = JSON.parse(dataString) as HostedFieldsPayload;

  return parsedData;
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  creditCardFormErrorSave: {
    defaultMessage: 'We were unable to save your card information. Please try again.',
    description: 'Credit Card Form | Error | Save',
  },
});

// ─── Types ─────────────────────────────────────────────────────────────────────

type StripeMessagesHandlerProps = Readonly<{
  onError: (userError?: string, systemError?: string) => void;
  onSuccess: (paymentMethodId: string) => void;
}>;

type HostedFieldsPayload = Readonly<{
  log?: string;
  loaded?: boolean;
  saving?: boolean;
  paymentMethodId?: string;
  error?: { message?: string };
  canSubmitForm?: boolean;
}>;
